import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, X, Maximize2, TrendingUp, Users, Brain,
  Heart, Target, Award, Activity, BarChart3, Zap, CheckCircle, AlertTriangle,
  ArrowRight, Globe, Download, Loader2
} from 'lucide-react';

const BoardPresentation = ({ onClose, roiData, benchmarkData, qbrData, sentimentData, goalsData, forecastData, pulseData, orgName, deptLeaderboard, apiUrl, getToken }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const totalSlides = 8;

  const goToSlide = useCallback((idx) => {
    if (isAnimating || idx < 0 || idx >= totalSlides) return;
    setIsAnimating(true);
    setCurrentSlide(idx);
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating, totalSlides]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goToSlide(currentSlide + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goToSlide(currentSlide - 1); }
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentSlide, goToSlide, onClose]);

  const SlideWrapper = ({ children, bg }) => (
    <div className="w-full h-full flex flex-col justify-center items-center px-16 py-12 relative" style={{ background: bg || '#FAFAF8' }}>
      {children}
    </div>
  );

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const quarterStr = `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}`;

  const slides = [
    // ── SLIDE 0: TITLE ──────────────────────────────────────────
    () => (
      <SlideWrapper bg="linear-gradient(160deg, #1A1209 0%, #2D1F0E 40%, #3D2E1A 100%)">
        <div className="text-center max-w-3xl">
          <div className="w-20 h-20 rounded-2xl mx-auto mb-8 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F5831F, #E06B10)', boxShadow: '0 8px 32px rgba(245,131,31,0.3)' }}>
            <span className="text-3xl font-black text-white">H</span>
          </div>
          <p className="text-sm font-semibold uppercase tracking-[6px] mb-4" style={{ color: '#F5831F' }}>Executive Briefing</p>
          <h1 className="text-5xl font-black text-white mb-3 leading-tight">{orgName || 'Organisation'}</h1>
          <p className="text-xl" style={{ color: '#A89880' }}>HiBFF Social Intelligence Platform</p>
          <div className="flex items-center justify-center gap-4 mt-8">
            <span className="px-4 py-1.5 rounded-full text-sm font-medium" style={{ backgroundColor: '#F5831F22', color: '#F5831F' }}>{dateStr}</span>
            <span className="px-4 py-1.5 rounded-full text-sm font-medium" style={{ backgroundColor: '#F5831F22', color: '#F5831F' }}>{quarterStr}</span>
          </div>
          <p className="text-xs mt-12" style={{ color: '#665E53' }}>Press <span className="px-2 py-0.5 rounded bg-white/10 text-white font-mono">Space</span> or <span className="px-2 py-0.5 rounded bg-white/10 text-white font-mono">Arrow Keys</span> to navigate</p>
        </div>
      </SlideWrapper>
    ),

    // ── SLIDE 1: ROI IMPACT ─────────────────────────────────────
    () => (
      <SlideWrapper>
        <div className="w-full max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-[4px] mb-2" style={{ color: '#F5831F' }}>Return on Investment</p>
          <h2 className="text-3xl font-black mb-8" style={{ color: '#1A1209' }}>Platform Impact Summary</h2>

          <div className="rounded-2xl p-8 mb-8" style={{ background: 'linear-gradient(135deg, #1A1209, #3D2E1A)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#F5831F' }}>Total Annual Return</p>
                <p className="text-6xl font-black" style={{ color: '#10B981' }}>${roiData?.total_annual_savings?.toLocaleString() || '—'}</p>
              </div>
              <div className="text-right space-y-2">
                <div className="px-5 py-2 rounded-xl" style={{ backgroundColor: '#F5831F22' }}>
                  <p className="text-3xl font-black" style={{ color: '#F5831F' }}>{roiData?.roi_multiple || '—'}x</p>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: '#A89880' }}>ROI Multiple</p>
                </div>
                <div className="px-5 py-2 rounded-xl" style={{ backgroundColor: '#10B98122' }}>
                  <p className="text-xl font-black" style={{ color: '#10B981' }}>{roiData?.payback_period_months || '—'} months</p>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: '#A89880' }}>Payback Period</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            {roiData?.savings_breakdown?.map((s, i) => {
              const colors = ['#10B981', '#3B82F6', '#F59E0B', '#8B5CF6'];
              return (
                <div key={i} className="bg-white rounded-xl p-5 text-center" style={{ border: '1px solid #E8E8E8' }}>
                  <p className="text-2xl font-black" style={{ color: colors[i] }}>${s.amount?.toLocaleString()}</p>
                  <p className="text-xs font-bold mt-1" style={{ color: '#1A1209' }}>{s.category}</p>
                  <p className="text-[10px] mt-1" style={{ color: '#999' }}>{s.percentage}% impact</p>
                </div>
              );
            })}
          </div>
        </div>
      </SlideWrapper>
    ),

    // ── SLIDE 2: BENCHMARKING ───────────────────────────────────
    () => (
      <SlideWrapper>
        <div className="w-full max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-[4px] mb-2" style={{ color: '#8B5CF6' }}>Industry Benchmarking</p>
          <h2 className="text-3xl font-black mb-2" style={{ color: '#1A1209' }}>How We Compare</h2>
          <p className="text-sm mb-8" style={{ color: '#999' }}>Benchmarked against {benchmarkData?.total_companies_benchmarked || 0}+ {benchmarkData?.industry || ''} companies</p>

          <div className="grid grid-cols-4 gap-4 mb-8">
            {[
              { key: 'engagement_rate', label: 'Engagement', unit: '%', color: '#F5831F' },
              { key: 'ocean_completion', label: 'OCEAN Done', unit: '%', color: '#8B5CF6' },
              { key: 'pulse_score', label: 'Pulse Score', unit: '/5', color: '#EC4899' },
              { key: 'retention_rate', label: 'Retention', unit: '%', color: '#059669' },
            ].map(m => {
              const yours = benchmarkData?.your_metrics?.[m.key] || 0;
              const pct = benchmarkData?.percentiles?.[m.key] || 0;
              return (
                <div key={m.key} className="bg-white rounded-xl p-5 text-center relative overflow-hidden" style={{ border: '1px solid #E8E8E8' }}>
                  <div className="absolute top-0 left-0 w-full h-1" style={{ backgroundColor: m.color }} />
                  <p className="text-4xl font-black" style={{ color: m.color }}>{yours}{m.unit}</p>
                  <p className="text-xs font-bold mt-1" style={{ color: '#1A1209' }}>{m.label}</p>
                  <p className="text-sm font-bold mt-2 px-3 py-1 rounded-full inline-block" style={{
                    backgroundColor: pct >= 75 ? '#ECFDF5' : pct >= 50 ? '#FFFBEB' : '#FEF2F2',
                    color: pct >= 75 ? '#059669' : pct >= 50 ? '#F59E0B' : '#DC2626',
                  }}>{pct}th percentile</p>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="rounded-xl p-5" style={{ backgroundColor: '#F0FDF4', border: '1px solid #D1FAE5' }}>
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#065F46' }}>
                <TrendingUp size={16} style={{ color: '#10B981' }} /> Competitive Advantages
              </h3>
              <div className="space-y-2">
                {benchmarkData?.advantages?.slice(0, 4).map(a => (
                  <div key={a.metric} className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: '#166534' }}>{a.metric}</span>
                    <span className="text-sm font-bold" style={{ color: '#059669' }}>{a.diff} vs avg</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#92400E' }}>
                <Target size={16} style={{ color: '#F59E0B' }} /> Growth Opportunities
              </h3>
              <div className="space-y-2">
                {benchmarkData?.gaps?.slice(0, 4).map(g => (
                  <div key={g.metric} className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: '#B45309' }}>{g.metric}</span>
                    <span className="text-sm font-bold" style={{ color: '#F59E0B' }}>{g.diff} vs avg</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </SlideWrapper>
    ),

    // ── SLIDE 3: QBR SUMMARY ────────────────────────────────────
    () => (
      <SlideWrapper>
        <div className="w-full max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-[4px] mb-2" style={{ color: '#6366F1' }}>Quarterly Business Review</p>
          <h2 className="text-3xl font-black mb-8" style={{ color: '#1A1209' }}>{qbrData?.quarter || quarterStr} Performance</h2>

          <div className="grid grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Members', value: qbrData?.executive_summary?.total_members, color: '#1A1209', icon: Users },
              { label: 'Total Sessions', value: qbrData?.executive_summary?.total_sessions?.toLocaleString(), color: '#3B82F6', icon: Activity },
              { label: 'Sessions / Member', value: qbrData?.executive_summary?.sessions_per_member, color: '#059669', icon: Zap },
              { label: 'Participation', value: `${qbrData?.executive_summary?.active_participation_pct}%`, color: '#F5831F', icon: TrendingUp },
            ].map(m => (
              <div key={m.label} className="bg-white rounded-xl p-6 text-center" style={{ border: '1px solid #E8E8E8' }}>
                <m.icon size={20} style={{ color: m.color }} className="mx-auto mb-2" />
                <p className="text-4xl font-black" style={{ color: m.color }}>{m.value || '—'}</p>
                <p className="text-xs font-medium mt-1" style={{ color: '#999' }}>{m.label}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-6">
            {qbrData?.key_highlights?.length > 0 && (
              <div className="rounded-xl p-5" style={{ backgroundColor: '#F0FDF4', border: '1px solid #D1FAE5' }}>
                <h3 className="text-sm font-bold mb-3" style={{ color: '#065F46' }}>Key Highlights</h3>
                <ul className="space-y-2">
                  {qbrData.key_highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#047857' }}>
                      <CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#10B981' }} />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {qbrData?.recommendations?.length > 0 && (
              <div className="rounded-xl p-5" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                <h3 className="text-sm font-bold mb-3" style={{ color: '#92400E' }}>Recommendations</h3>
                <ul className="space-y-2">
                  {qbrData.recommendations.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#B45309' }}>
                      <ArrowRight size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </SlideWrapper>
    ),

    // ── SLIDE 4: TEAM WELLNESS ──────────────────────────────────
    () => (
      <SlideWrapper>
        <div className="w-full max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-[4px] mb-2" style={{ color: '#EC4899' }}>Team Wellness</p>
          <h2 className="text-3xl font-black mb-8" style={{ color: '#1A1209' }}>Pulse & Sentiment Overview</h2>

          <div className="grid grid-cols-3 gap-6 mb-8">
            <div className="rounded-2xl p-8 text-center" style={{ background: 'linear-gradient(135deg, #1A1209, #3D2E1A)' }}>
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#F5831F' }}>Pulse Score</p>
              <p className="text-6xl font-black" style={{ color: pulseData?.overall_score >= 4 ? '#10B981' : pulseData?.overall_score >= 3.5 ? '#F59E0B' : '#EF4444' }}>
                {pulseData?.overall_score || '—'}
              </p>
              <p className="text-sm mt-1" style={{ color: '#A89880' }}>out of 5.0</p>
            </div>
            <div className="rounded-2xl p-8 text-center" style={{ background: 'linear-gradient(135deg, #1A1209, #3D2E1A)' }}>
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#F5831F' }}>Sentiment</p>
              <p className="text-6xl font-black" style={{ color: sentimentData?.current_score >= 3.8 ? '#10B981' : '#F59E0B' }}>
                {sentimentData?.current_score || '—'}
              </p>
              <p className="text-sm mt-1" style={{ color: '#A89880' }}>
                {sentimentData?.trend_direction === 'up' ? 'Trending Up' : sentimentData?.trend_direction === 'down' ? 'Trending Down' : 'Stable'}
              </p>
            </div>
            <div className="rounded-2xl p-8 text-center" style={{ background: 'linear-gradient(135deg, #1A1209, #3D2E1A)' }}>
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: '#F5831F' }}>Participation</p>
              <p className="text-6xl font-black" style={{ color: '#3B82F6' }}>
                {pulseData?.participation_rate || '—'}%
              </p>
              <p className="text-sm mt-1" style={{ color: '#A89880' }}>Response Rate</p>
            </div>
          </div>

          {pulseData?.results?.length > 0 && (
            <div className="bg-white rounded-xl p-6" style={{ border: '1px solid #E8E8E8' }}>
              <h3 className="text-sm font-bold mb-4" style={{ color: '#1A1209' }}>Question-by-Question Breakdown</h3>
              <div className="space-y-3">
                {pulseData.results.map((q, i) => {
                  const pct = (q.week_avg / 5) * 100;
                  const color = q.week_avg >= 4 ? '#10B981' : q.week_avg >= 3.5 ? '#F59E0B' : '#EF4444';
                  return (
                    <div key={i} className="flex items-center gap-4">
                      <span className="text-sm flex-1" style={{ color: '#666' }}>{q.question}</span>
                      <div className="w-48 h-3 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                      </div>
                      <span className="text-sm font-black w-10 text-right" style={{ color }}>{q.week_avg}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SlideWrapper>
    ),

    // ── SLIDE 5: ENGAGEMENT FORECAST ────────────────────────────
    () => (
      <SlideWrapper>
        <div className="w-full max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-[4px] mb-2" style={{ color: '#6366F1' }}>Predictive Analytics</p>
          <h2 className="text-3xl font-black mb-2" style={{ color: '#1A1209' }}>12-Week Engagement Forecast</h2>
          <p className="text-sm mb-8" style={{ color: '#999' }}>
            Trajectory: <span className="font-bold" style={{ color: forecastData?.trajectory === 'improving' ? '#059669' : forecastData?.trajectory === 'stable' ? '#3B82F6' : '#DC2626' }}>
              {forecastData?.trajectory === 'improving' ? 'Improving' : forecastData?.trajectory === 'stable' ? 'Stable' : 'Needs Attention'}
            </span>
            {forecastData?.change_predicted !== undefined && ` (${forecastData.change_predicted > 0 ? '+' : ''}${forecastData.change_predicted}% projected)`}
          </p>

          <div className="bg-white rounded-xl p-8" style={{ border: '1px solid #E8E8E8' }}>
            <div className="flex items-end gap-1.5 h-48">
              {[...(forecastData?.historical || []), ...(forecastData?.forecast || [])].map((w, i) => {
                const maxVal = 100;
                const height = Math.max(8, (w.engagement / maxVal) * 100);
                const isForecast = w.type === 'forecast';
                const color = isForecast ? '#6366F1' : '#1A1209';
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[9px] font-bold" style={{ color }}>{w.engagement}%</span>
                    <div className="w-full rounded-t-md" style={{
                      height: `${height}%`,
                      backgroundColor: color,
                      opacity: isForecast ? 0.3 + (w.confidence || 0.5) * 0.6 : 0.85,
                    }} />
                    <span className="text-[8px]" style={{ color: '#CCC' }}>{w.date || w.week}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center gap-8 mt-6 pt-4" style={{ borderTop: '1px solid #F0F0F0' }}>
              <div className="flex items-center gap-2"><div className="w-5 h-3 rounded" style={{ backgroundColor: '#1A1209', opacity: 0.85 }} /><span className="text-xs" style={{ color: '#999' }}>Historical</span></div>
              <div className="flex items-center gap-2"><div className="w-5 h-3 rounded" style={{ backgroundColor: '#6366F1', opacity: 0.6 }} /><span className="text-xs" style={{ color: '#999' }}>Forecast (AI-predicted)</span></div>
            </div>
          </div>
        </div>
      </SlideWrapper>
    ),

    // ── SLIDE 6: GOALS & OKR PROGRESS ───────────────────────────
    () => (
      <SlideWrapper>
        <div className="w-full max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-[4px] mb-2" style={{ color: '#F59E0B' }}>Objectives & Key Results</p>
          <h2 className="text-3xl font-black mb-8" style={{ color: '#1A1209' }}>Goal Progress</h2>

          {goalsData?.goals?.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {goalsData.goals.map(goal => {
                const pct = goal.target_value > 0 ? Math.min(Math.round((goal.current_value / goal.target_value) * 100), 100) : 0;
                const statusColors = {
                  on_track: { bar: '#10B981', text: '#059669', label: 'On Track', bg: '#F0FDF4' },
                  at_risk: { bar: '#F59E0B', text: '#F59E0B', label: 'At Risk', bg: '#FFFBEB' },
                  behind: { bar: '#EF4444', text: '#DC2626', label: 'Behind', bg: '#FEF2F2' },
                  not_started: { bar: '#D1D5DB', text: '#9CA3AF', label: 'Not Started', bg: '#F8F7F4' },
                };
                const s = statusColors[goal.status] || statusColors.not_started;
                return (
                  <div key={goal.goal_id} className="bg-white rounded-xl p-6 flex items-center gap-6" style={{ border: '1px solid #E8E8E8' }}>
                    <div className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.bg }}>
                      <span className="text-2xl font-black" style={{ color: s.bar }}>{pct}%</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-base font-bold" style={{ color: '#1A1209' }}>{goal.title}</h4>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: s.bg, color: s.text }}>{s.label}</span>
                      </div>
                      <p className="text-xs mb-2" style={{ color: '#999' }}>{goal.description}</p>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-3 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: s.bar }} />
                        </div>
                        <span className="text-xs font-bold" style={{ color: '#666' }}>{goal.current_value}{goal.unit} / {goal.target_value}{goal.unit}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-xl p-12 text-center" style={{ border: '1px solid #E8E8E8' }}>
              <Target size={40} style={{ color: '#DDD' }} className="mx-auto mb-3" />
              <p className="text-lg" style={{ color: '#999' }}>No goals set yet</p>
            </div>
          )}
        </div>
      </SlideWrapper>
    ),

    // ── SLIDE 7: CLOSING ────────────────────────────────────────
    () => (
      <SlideWrapper bg="linear-gradient(160deg, #1A1209 0%, #2D1F0E 40%, #3D2E1A 100%)">
        <div className="text-center max-w-3xl">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F5831F, #E06B10)' }}>
            <span className="text-2xl font-black text-white">H</span>
          </div>
          <h2 className="text-4xl font-black text-white mb-4">Thank You</h2>
          <p className="text-lg mb-8" style={{ color: '#A89880' }}>
            {orgName || 'Organisation'} is building a culture of genuine human connection.
          </p>

          <div className="grid grid-cols-3 gap-4 mb-10">
            {[
              { value: `$${((roiData?.total_annual_savings || 0) / 1000).toFixed(0)}K`, label: 'Annual Savings', color: '#10B981' },
              { value: `${roiData?.roi_multiple || '—'}x`, label: 'ROI Multiple', color: '#F5831F' },
              { value: `P${benchmarkData?.percentiles?.engagement_rate || '—'}`, label: 'Industry Percentile', color: '#8B5CF6' },
            ].map(m => (
              <div key={m.label} className="rounded-xl p-5" style={{ backgroundColor: '#FFFFFF08', border: '1px solid #FFFFFF15' }}>
                <p className="text-3xl font-black" style={{ color: m.color }}>{m.value}</p>
                <p className="text-xs mt-1" style={{ color: '#A89880' }}>{m.label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 text-sm" style={{ color: '#665E53' }}>
            <Globe size={14} />
            <span>hibff.com</span>
            <span className="mx-2">|</span>
            <span>Powered by HiBFF Social Intelligence</span>
          </div>
        </div>
      </SlideWrapper>
    ),
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black" data-testid="board-presentation">
      {/* Slide content */}
      <div className="w-full h-full relative overflow-hidden">
        <div className="w-full h-full transition-opacity duration-300" style={{ opacity: isAnimating ? 0.5 : 1 }}>
          {slides[currentSlide]()}
        </div>
      </div>

      {/* Navigation controls */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-8 py-4" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.5))' }}>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 text-white/70 hover:text-white transition-colors" data-testid="exit-presentation-btn">
            <X size={14} /> Exit
          </button>
          <button onClick={async () => {
            setDownloading(true);
            try {
              const res = await fetch(`${apiUrl}/api/corporate/export/board-pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
              if (res.ok) {
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `HiBFF_Executive_Briefing_${(orgName || 'Report').replace(/\s+/g, '_')}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              }
            } catch {}
            setDownloading(false);
          }} disabled={downloading}
            className="px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 transition-colors"
            style={{ backgroundColor: 'rgba(245,131,31,0.2)', color: '#F5831F' }}
            data-testid="download-pdf-btn">
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {downloading ? 'Generating...' : 'Download PDF'}
          </button>
        </div>

        <div className="flex items-center gap-2">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button key={i} onClick={() => goToSlide(i)}
              className="transition-all rounded-full"
              style={{
                width: i === currentSlide ? 24 : 8,
                height: 8,
                backgroundColor: i === currentSlide ? '#F5831F' : 'rgba(255,255,255,0.3)',
              }}
              data-testid={`slide-dot-${i}`} />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => goToSlide(currentSlide - 1)} disabled={currentSlide === 0}
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/70 hover:text-white disabled:opacity-20 transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} data-testid="prev-slide-btn">
            <ChevronLeft size={18} />
          </button>
          <span className="text-xs font-mono text-white/50 w-12 text-center">{currentSlide + 1}/{totalSlides}</span>
          <button onClick={() => goToSlide(currentSlide + 1)} disabled={currentSlide === totalSlides - 1}
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/70 hover:text-white disabled:opacity-20 transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} data-testid="next-slide-btn">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Slide title indicator */}
      <div className="absolute top-4 right-4">
        <span className="text-xs font-medium px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>
          {['Title', 'ROI Impact', 'Benchmarking', 'QBR Summary', 'Team Wellness', 'Engagement Forecast', 'Goals & OKRs', 'Closing'][currentSlide]}
        </span>
      </div>
    </div>
  );
};

export default BoardPresentation;
